import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PERIOD_LABELS } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const FORMULES = [
  { value: "journee", label: "Une journée (3 ateliers)", price: 480 },
  { value: "mercredi", label: "Une matinée (2 ateliers)", price: 380 },
  { value: "atelier", label: "1 atelier", price: 220 },
];
const ZONES = [
  { value: "lln", label: "Louvain-la-Neuve (25 km)", fee: 40, parking: 0 },
  { value: "charleroi", label: "Charleroi (30 km)", fee: 45, parking: 0 },
  { value: "liege", label: "Liège (30 km)", fee: 50, parking: 0 },
  { value: "bruxelles", label: "Région de Bruxelles-Capitale (30 km)", fee: 50, parking: 20 },
  { value: "tournai", label: "Tournai (40 km)", fee: 50, parking: 0 },
  { value: "autre", label: "Autre / hors zone (sur devis)", fee: null, parking: 0 },
];
const MATERIAL_PER_STUDENT = 1;

const emptyAtelier = (id = "", title = "") => ({
  item_id: id,
  item_title: title,
  school_year: "",
  class_name: "",
  preferred_date: "",
  students: "",
  formule: "",
  travel_zone: "",
  has_computer_room: "",
  slot_id: "",
});

// Coûts d'un atelier
function computeCosts(a) {
  const fo = FORMULES.find((f) => f.value === a.formule);
  const zo = ZONES.find((z) => z.value === a.travel_zone);
  const forfait = fo ? fo.price : 0;
  const custom = !!(zo && zo.fee == null);
  const travel = zo && zo.fee != null ? zo.fee + (zo.parking || 0) : 0;
  const students = a.students ? parseInt(a.students, 10) : 0;
  const material = a.has_computer_room === "non" ? MATERIAL_PER_STUDENT * students : 0;
  return { fo, zo, forfait, custom, travel, students, material, total: forfait + travel + material };
}

export default function ReservationDialog({ kind, itemId, itemTitle, trigger, prefill = null }) {
  const isAtelier = kind === "atelier";
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [slots, setSlots] = useState([]);
  const [allWorkshops, setAllWorkshops] = useState([]);
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "", school: "" });
  const [message, setMessage] = useState("");
  const [cur, setCur] = useState(emptyAtelier(itemId, itemTitle));
  const [basket, setBasket] = useState([]); // ateliers déjà ajoutés

  useEffect(() => {
    if (open && isAtelier) {
      api.get("/availability/free").then((r) => setSlots(r.data)).catch(() => {});
      api.get("/workshops").then((r) => setAllWorkshops(r.data)).catch(() => {});
    }
  }, [open, isAtelier]);

  useEffect(() => {
    if (open && prefill) {
      setContact((c) => ({ ...c, school: prefill.school || c.school, email: prefill.email || c.email }));
    }
  }, [open, prefill]);

  const updateContact = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));
  const updateCur = (k) => (e) => setCur((a) => ({ ...a, [k]: e.target.value }));
  const setCurField = (k, v) => setCur((a) => ({ ...a, [k]: v }));

  const curCosts = computeCosts(cur);

  const grandTotal = [...basket, ...(cur.item_id && cur.formule ? [cur] : [])]
    .reduce((sum, a) => sum + computeCosts(a).total, 0);
  const anyCustom = [...basket, ...(cur.item_id && cur.formule ? [cur] : [])]
    .some((a) => computeCosts(a).custom);

  const validateAtelier = (a) => {
    if (!a.item_id) return "Choisissez l'atelier concerné";
    if (!a.school_year) return "Indiquez l'année scolaire";
    if (!a.class_name) return "Indiquez la classe";
    if (!a.formule) return "Choisissez une formule";
    if (!a.travel_zone) return "Choisissez la zone de déplacement";
    if (!a.has_computer_room) return "Indiquez si l'école dispose d'une salle informatique";
    return null;
  };

  const addAtelier = () => {
    const err = validateAtelier(cur);
    if (err) { toast.error(err); return; }
    setBasket((b) => [...b, cur]);
    // liste des ateliers restants pour repartir sur un choix vierge
    setCur(emptyAtelier("", ""));
    toast.success("Atelier ajouté. Choisissez-en un autre puis remplissez ses informations.");
  };

  const removeFromBasket = (idx) => setBasket((b) => b.filter((_, i) => i !== idx));

  const payloadFor = (a) => {
    const c = computeCosts(a);
    const chosen = slots.find((s) => s.id === a.slot_id);
    return {
      kind,
      item_id: a.item_id,
      item_title: a.item_title,
      contact_name: `${contact.first_name} ${contact.last_name}`.trim(),
      email: contact.email,
      phone: contact.phone || null,
      school: contact.school || null,
      school_year: a.school_year || null,
      class_name: a.class_name || null,
      preferred_date: chosen ? chosen.date : a.preferred_date || null,
      students: a.students ? parseInt(a.students, 10) : null,
      formule: c.fo ? c.fo.label : null,
      formule_price: c.fo ? c.fo.price : null,
      travel_zone: c.zo ? c.zo.label : null,
      travel_fee: c.zo && c.zo.fee != null ? c.travel : null,
      material_fee: a.has_computer_room === "non" ? c.material : null,
      has_computer_room: a.has_computer_room || null,
      estimated_total: c.fo && !c.custom ? c.total : null,
      message: message || null,
      slot_id: a.slot_id || null,
    };
  };

  const downloadQuote = async () => {
    const c = curCosts;
    if (!c.fo) { toast.error("Choisissez d'abord une formule"); return; }
    setQuoting(true);
    try {
      const res = await api.post("/reservations/quote-pdf", {
        item_title: cur.item_title,
        contact_name: `${contact.first_name} ${contact.last_name}`.trim() || null,
        school: contact.school || null,
        school_year: cur.school_year || null,
        class_name: cur.class_name || null,
        students: c.students || null,
        formule: c.fo.label,
        formule_price: c.forfait,
        travel_zone: c.zo ? c.zo.label : null,
        travel_fee: c.zo && c.zo.fee != null ? c.travel : null,
        material_fee: c.material || null,
        estimated_total: c.custom ? null : c.total,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Devis_Kwarizmix_${(cur.item_title || "atelier").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Impossible de générer le devis");
    } finally {
      setQuoting(false);
    }
  };

  const resetAll = () => {
    setBasket([]);
    setCur(emptyAtelier(itemId, itemTitle));
    setContact({ first_name: "", last_name: "", email: "", phone: "", school: "" });
    setMessage("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.first_name || !contact.last_name) { toast.error("Indiquez votre prénom et votre nom"); return; }
    if (!contact.email) { toast.error("Indiquez votre email"); return; }

    let entries;
    if (isAtelier) {
      // l'atelier en cours compte s'il est renseigné
      const curFilled = cur.item_id && (cur.formule || cur.school_year);
      entries = [...basket];
      if (curFilled) {
        const err = validateAtelier(cur);
        if (err) { toast.error(err); return; }
        entries.push(cur);
      }
      if (entries.length === 0) { toast.error("Ajoutez au moins un atelier"); return; }
    } else {
      entries = [cur];
    }

    setLoading(true);
    try {
      for (const a of entries) {
        await api.post("/reservations", payloadFor(a));
      }
      toast.success(
        entries.length > 1
          ? `${entries.length} ateliers envoyés ! Un email de confirmation vous a été adressé.`
          : "Demande envoyée ! Un email de confirmation vous a été adressé."
      );
      setOpen(false);
      resetAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // ateliers sélectionnables (on retire ceux déjà dans le panier)
  const chosenIds = new Set(basket.map((a) => a.item_id));
  const selectableWorkshops = allWorkshops.filter((w) => !chosenIds.has(w.id));

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v && isAtelier) setCur(emptyAtelier(itemId, itemTitle)); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Réserver : {itemTitle}</DialogTitle>
          <DialogDescription>Merci de remplir le formulaire suivant</DialogDescription>
        </DialogHeader>
        <motion.form
          onSubmit={submit}
          className="space-y-4 mt-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid="reservation-form"
        >
          {/* Coordonnées (communes à toute la commande) */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Prénom *</Label>
              <Input required value={contact.first_name} onChange={updateContact("first_name")} className="rounded-xl mt-1" data-testid="res-firstname" />
            </div>
            <div>
              <Label>Nom *</Label>
              <Input required value={contact.last_name} onChange={updateContact("last_name")} className="rounded-xl mt-1" data-testid="res-lastname" />
            </div>
          </div>
          <div>
            <Label>Email *</Label>
            <Input required type="email" value={contact.email} onChange={updateContact("email")} className="rounded-xl mt-1" data-testid="res-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Téléphone</Label>
              <Input value={contact.phone} onChange={updateContact("phone")} className="rounded-xl mt-1" data-testid="res-phone" />
            </div>
            <div>
              <Label>École / organisation</Label>
              <Input value={contact.school} onChange={updateContact("school")} className="rounded-xl mt-1" data-testid="res-school" />
            </div>
          </div>

          {/* Ateliers déjà ajoutés au panier */}
          {isAtelier && basket.length > 0 && (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 space-y-2" data-testid="res-basket">
              <p className="text-sm font-bold text-emerald-800">Ateliers ajoutés ({basket.length})</p>
              {basket.map((a, i) => {
                const c = computeCosts(a);
                return (
                  <div key={`${a.item_id}-${i}`} className="flex items-center justify-between gap-3 text-sm" data-testid={`res-basket-item-${i}`}>
                    <span className="text-slate-700 truncate">{a.item_title} · {a.school_year} {a.class_name && `· ${a.class_name}`}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="font-bold text-slate-800">{c.custom ? "Sur devis" : `${c.total} €`}</span>
                      <button type="button" onClick={() => removeFromBasket(i)} data-testid={`res-basket-remove-${i}`} className="text-rose-500 hover:text-rose-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sélecteur d'atelier (choisir parmi les autres, 1 à la fois) */}
          {isAtelier && (
            <div>
              <Label>Atelier concerné *</Label>
              <Select value={cur.item_id} onValueChange={(v) => { const w = allWorkshops.find((x) => x.id === v); setCur((a) => ({ ...a, item_id: v, item_title: w ? w.title : a.item_title })); }}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="res-workshop-select"><SelectValue placeholder="Choisir un atelier" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {selectableWorkshops.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Champs spécifiques à l'atelier en cours */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Année scolaire *</Label>
              <Select value={cur.school_year} onValueChange={(v) => setCurField("school_year", v)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="res-year"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3e primaire">3e primaire</SelectItem>
                  <SelectItem value="4e primaire">4e primaire</SelectItem>
                  <SelectItem value="5e primaire">5e primaire</SelectItem>
                  <SelectItem value="6e primaire">6e primaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Classe *</Label>
              <Input value={cur.class_name} onChange={updateCur("class_name")} placeholder="ex: 4B" className="rounded-xl mt-1" data-testid="res-classname" />
            </div>
          </div>

          {isAtelier && slots.length > 0 && (
            <div>
              <Label>Dates disponibles (animateurs)</Label>
              <Select value={cur.slot_id} onValueChange={(v) => setCurField("slot_id", v)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="res-slot">
                  <SelectValue placeholder="Choisir un créneau disponible" />
                </SelectTrigger>
                <SelectContent>
                  {slots.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {new Date(s.date).toLocaleDateString("fr-BE", { day: "numeric", month: "long", year: "numeric" })} · {PERIOD_LABELS[s.period || "matin"]} · {s.animator_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Ou indiquez une date libre ci-dessous.</p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Date souhaitée</Label>
              <Input value={cur.preferred_date} onChange={updateCur("preferred_date")} placeholder="ex: 15 sept. 2026" className="rounded-xl mt-1" data-testid="res-date" />
            </div>
            <div>
              <Label>Nombre d'élèves</Label>
              <Select value={cur.students} onValueChange={(v) => setCurField("students", v)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="res-students"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {Array.from({ length: 50 }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isAtelier && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Formule *</Label>
                <Select value={cur.formule} onValueChange={(v) => setCurField("formule", v)}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="res-formule"><SelectValue placeholder="Choisir une formule" /></SelectTrigger>
                  <SelectContent>
                    {FORMULES.map((o) => <SelectItem key={o.value} value={o.value}>{o.price} € — {o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zone (déplacement) *</Label>
                <Select value={cur.travel_zone} onValueChange={(v) => setCurField("travel_zone", v)}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="res-zone"><SelectValue placeholder="Choisir une zone" /></SelectTrigger>
                  <SelectContent>
                    {ZONES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}{o.fee != null ? ` — ${o.fee} €` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {isAtelier && (
            <div>
              <Label>L'école dispose-t-elle d'une salle informatique ? *</Label>
              <Select value={cur.has_computer_room} onValueChange={(v) => setCurField("has_computer_room", v)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="res-salle-info"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oui">Oui (pas de supplément matériel)</SelectItem>
                  <SelectItem value="non">Non (supplément matériel : 1 € / élève)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {isAtelier && cur.formule && (
            <div className="rounded-2xl bg-sky-50 border border-sky-100 p-4" data-testid="res-total-box">
              <div className="flex justify-between text-sm text-slate-600"><span>Forfait ({curCosts.fo ? curCosts.fo.label : ""})</span><span>{curCosts.forfait} €</span></div>
              {curCosts.zo && curCosts.zo.fee != null && (
                <div className="flex justify-between text-sm text-slate-600 mt-1">
                  <span>Déplacement{curCosts.zo.parking ? ` + parking ${curCosts.zo.parking} €` : ""}</span><span>{curCosts.travel} €</span>
                </div>
              )}
              {curCosts.material > 0 && (
                <div className="flex justify-between text-sm text-slate-600 mt-1">
                  <span>Supplément matériel ({curCosts.students} × 1 €)</span><span>{curCosts.material} €</span>
                </div>
              )}
              <div className="flex justify-between font-heading font-extrabold text-slate-900 text-lg mt-2 pt-2 border-t border-sky-100">
                <span>Total de cet atelier</span>
                <span data-testid="res-total-amount">{curCosts.custom ? "Sur devis" : `${curCosts.total} €`}</span>
              </div>
              <button type="button" onClick={downloadQuote} disabled={quoting} data-testid="res-download-quote" className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-sky-600 hover:text-sky-700 disabled:opacity-60">
                <Download className="w-4 h-4" /> {quoting ? "Génération..." : "Télécharger le devis (PDF)"}
              </button>
            </div>
          )}

          <div>
            <Label>Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-xl mt-1" rows={3} data-testid="res-message" />
          </div>

          {/* Total global de la commande */}
          {isAtelier && (basket.length > 0 || grandTotal > 0) && (
            <div className="flex items-center justify-between rounded-2xl bg-slate-900 text-white px-5 py-4" data-testid="res-grand-total">
              <span className="font-bold">Total estimé</span>
              <span className="font-heading font-black text-xl" data-testid="res-grand-total-amount">
                {grandTotal > 0 ? `${grandTotal} €` : "—"}{anyCustom ? " + sur devis" : ""}
              </span>
            </div>
          )}

          {isAtelier ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={addAtelier}
                data-testid="res-add-workshop"
                className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full border-2 border-sky-500 text-sky-600 font-bold hover:bg-sky-50 transition-colors"
              >
                <Plus className="w-5 h-5" /> Rajouter un atelier
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="res-submit"
                className="flex-1 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-[1.02] transition-[transform,background-color] disabled:opacity-60"
              >
                {loading ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          ) : (
            <button
              type="submit"
              disabled={loading}
              data-testid="res-submit"
              className="w-full py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-[1.02] transition-[transform,background-color] disabled:opacity-60"
            >
              {loading ? "Envoi..." : "Envoyer ma demande"}
            </button>
          )}
        </motion.form>
      </DialogContent>
    </Dialog>
  );
}
